import { KpiCard } from "@/components/kpi-card";
import { Card, CardContent } from "@/components/ui/card";
import type { ActivityAnalytics, AdminUserSummary } from "@/lib/api";

import { ActivityChart } from "./activity-chart";
import { ActivityExplorer } from "./activity-explorer";

interface Props {
  analytics: ActivityAnalytics;
  users: AdminUserSummary[];
  actions: string[];
}

export function ActivityDashboard({ analytics, users, actions }: Props) {
  const { summary, timeseries, breakdown } = analytics;

  const stats = [
    { label: "Events", value: summary.total_events },
    { label: "Logins", value: summary.total_logins },
    { label: "Active users", value: summary.active_users },
    { label: "Signups", value: summary.signups },
  ];

  return (
    <div className="space-y-8">
      <section>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((stat) => (
            <KpiCard
              key={stat.label}
              label={stat.label}
              value={stat.value}
              caption={`Last ${summary.days} days`}
            />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
          Activity over time
        </h3>
        <Card>
          <CardContent className="py-6">
            <ActivityChart data={timeseries} />
            <div className="text-muted mt-3 flex items-center justify-between text-xs">
              <span className="font-mono text-[10px] tabular-nums">{timeseries[0]?.day}</span>
              <span className="flex items-center gap-3">
                <span className="text-muted inline-flex items-center gap-1.5 text-[11px]">
                  <span className="bg-jj-blue-03 h-2.5 w-2.5 rounded-sm" /> logins
                </span>
                <span className="text-muted inline-flex items-center gap-1.5 text-[11px]">
                  <span className="bg-warm-gray-04 h-2.5 w-2.5 rounded-sm" /> other events
                </span>
              </span>
              <span className="font-mono text-[10px] tabular-nums">
                {timeseries[timeseries.length - 1]?.day}
              </span>
            </div>
          </CardContent>
        </Card>
      </section>

      {breakdown.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
            By action
          </h3>
          <Card>
            <CardContent className="space-y-2 py-4">
              {breakdown.map((item) => (
                <div key={item.action} className="flex items-center justify-between text-sm">
                  <span className="text-foreground font-medium">{item.action}</span>
                  <span className="text-foreground font-medium tabular-nums">{item.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      <section className="space-y-3">
        <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
          Recent activity
        </h3>
        <ActivityExplorer users={users} actions={actions} />
      </section>
    </div>
  );
}
